<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { ChevronDown, X } from 'lucide-vue-next';

interface Option {
  value: any;
  label: string;
}

const props = withDefaults(
  defineProps<{
    modelValue: any;
    options?: Option[];
    disabled?: boolean;
    noBorder?: boolean;
    error?: boolean;
    class?: string;
    menuClass?: string;
    triggerClass?: string;
    placeholder?: string;
    disableTeleport?: boolean;
    clearable?: boolean;
  }>(),
  {
    noBorder: false,
    options: () => [],
    error: false,
    disableTeleport: false,
    clearable: false,
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', value: any): void;
}>();

const isOpen = ref(false);
const dropdownRef = ref<HTMLElement>();
const buttonRef = ref<HTMLElement | null>(null);

const dropdownStyle = ref({
  top: '0px',
  left: '0px',
  width: '0px',
});

const selectedOption = computed(() => {
  if (props.options.length) {
    return props.options.find((opt) => opt.value === props.modelValue)?.label || props.modelValue;
  }
  return props.modelValue;
});

const updateDropdownPosition = () => {
  if (buttonRef.value) {
    const rect = buttonRef.value.getBoundingClientRect();
    if (props.disableTeleport) {
      // When teleport is disabled, use relative positioning
      dropdownStyle.value = {
        top: `${rect.height + 4}px`,
        left: '0px',
        width: `${rect.width}px`,
      };
    } else {
      // When teleport is enabled, use fixed positioning
      dropdownStyle.value = {
        top: `${rect.bottom + 4}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
      };
    }
  }
};

function handleClickOutside(event: MouseEvent) {
  const target = event.target as HTMLElement;
  if (!target.closest('.app-select-container')) {
    isOpen.value = false;
  }
}

function toggleDropdown() {
  if (props.disabled) return;
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    updateDropdownPosition();
  }
}

// Handle window resize and scroll
const handleResize = () => {
  if (isOpen.value) {
    updateDropdownPosition();
  }
};

function selectOption(value: any) {
  emit('update:modelValue', value);
  isOpen.value = false;
}

function clearSelection(event: MouseEvent) {
  event.stopPropagation();
  emit('update:modelValue', null);
  isOpen.value = false;
}

// Lifecycle hooks
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  window.addEventListener('resize', handleResize);
  window.addEventListener('scroll', handleResize, true);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  window.removeEventListener('resize', handleResize);
  window.removeEventListener('scroll', handleResize, true);
});
</script>

<template>
  <div
    class="app-select-container tw:relative"
    ref="dropdownRef">
    <!-- Trigger Button -->
    <div
      ref="buttonRef"
      class="tw:flex tw:w-full tw:cursor-pointer tw:items-center tw:justify-between tw:rounded-md tw:bg-white tw:px-3 tw:py-2"
      :class="[
        props.class,
        {
          'tw:border tw:border-solid tw:border-gray-300': !noBorder,
          'tw:cursor-not-allowed tw:opacity-50': disabled,
          'tw:border-red-500 tw:bg-red-50': error,
        },
      ]"
      @click="toggleDropdown">
      <!-- Selected Value Display -->
      <slot
        name="trigger"
        :value="selectedOption">
        <span
          class="tw:truncate tw:text-sm"
          :class="[
            triggerClass,
            { 'tw:text-gray-500': !modelValue }
          ]"
          >{{ modelValue ? selectedOption : props.placeholder }}</span
        >
      </slot>

      <X
        v-if="clearable && modelValue"
        class="tw:ml-1 tw:shrink-0 tw:text-gray-500 tw:hover:text-gray-600"
        :size="16"
        @click="clearSelection" />
      <ChevronDown
        v-else
        class="tw:ml-1 tw:shrink-0 tw:text-gray-500"
        :size="16"
        :class="{ 'tw:rotate-180': isOpen }" />
    </div>

    <!-- Teleported Dropdown Menu -->
    <Teleport
      to="body"
      :disabled="disableTeleport">
      <div
        v-if="isOpen"
        :style="dropdownStyle"
        :class="[
          menuClass,
          disableTeleport ? 'tw:absolute' : 'tw:fixed',
          'tw:z-9999 tw:max-h-60 tw:overflow-auto tw:rounded-md tw:border tw:border-gray-200 tw:bg-white tw:py-1 tw:text-xs tw:font-normal tw:shadow-lg',
        ]"
        data-testid="app-select-menu">
        <!-- Default Options Implementation -->
        <template v-if="options.length">
          <button
            v-for="option in options"
            :key="option.value"
            class="tw:w-full tw:px-3 tw:py-2.5 tw:text-left tw:hover:bg-gray-100"
            @click="selectOption(option.value)">
            {{ option.label }}
          </button>
        </template>

        <!-- Custom Options Implementation -->
        <slot
          name="menu"
          :select="selectOption"
          :value="selectedOption" />
      </div>
    </Teleport>
  </div>
</template>
